
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useOptimizedPlanRoutines = (planId?: string, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled !== false;
  
  return useQuery({
    queryKey: ["optimized-plan-routines", planId],
    queryFn: async () => {
      if (!planId) return [];

      console.log("🚀 Optimized plan routines query started for:", planId);
      const startTime = performance.now();

      // Query 1: Get routine IDs + position (fast, only IDs)
      const { data: links, error: linksError } = await supabase
        .from("workout_plan_routines")
        .select("routine_id, position")
        .eq("workout_plan_id", planId)
        .order("position", { ascending: true })
        .limit(20);

      if (linksError) throw linksError;
      if (!links?.length) {
        console.log("✅ No routines found for plan");
        return [];
      }

      const routineIds = links.map(l => l.routine_id);
      const positionMap = new Map(links.map(l => [l.routine_id, l.position]));
      console.log(`📋 Found ${routineIds.length} routine IDs`);

      // Queries 2, 3, 4: In parallel (no JOINs - much faster)
      const [routinesResult, daysResult, exercisesResult] = await Promise.all([
        // Basic routine data only
        supabase
          .from("routines")
          .select("id, name, image_url")
          .in("id", routineIds)
          .limit(20),
        
        // Days separately
        supabase
          .from("routine_days")
          .select("routine_id, day")
          .in("routine_id", routineIds),
        
        // Exercises separately (more efficient than JOIN)
        supabase
          .from("exercises")
          .select("id, name, sets, reps, weight, position, routine_id, set_data, exercise_rest_time, set_rest_time, video_url")
          .in("routine_id", routineIds)
          .order("position", { ascending: true })
          .limit(200)
      ]);

      if (routinesResult.error) throw routinesResult.error;
      if (daysResult.error) throw daysResult.error;
      if (exercisesResult.error) throw exercisesResult.error;

      console.log(`✅ Fetched ${exercisesResult.data?.length || 0} exercises`);

      // Combine in memory (fast)
      const routinesWithData = routinesResult.data?.map(routine => {
        const routineDayData = daysResult.data?.find(d => d.routine_id === routine.id);
        const routineExercises = exercisesResult.data?.filter(e => e.routine_id === routine.id) || [];

        const formattedExercises = routineExercises.map(exercise => ({
          ...exercise,
          exercise_rest_time: exercise.exercise_rest_time !== null ? Number(exercise.exercise_rest_time) : null,
          set_rest_time: exercise.set_rest_time !== null ? Number(exercise.set_rest_time) : null,
          set_data: exercise.set_data || []
        }));

        return {
          ...routine,
          day: routineDayData?.day || null,
          plan_position: positionMap.get(routine.id) ?? 0,
          exercises: formattedExercises
        };
      }).sort((a, b) => (a.plan_position ?? 0) - (b.plan_position ?? 0)) || [];

      const endTime = performance.now();
      console.log(`✅ Optimized query completed in ${(endTime - startTime).toFixed(2)}ms`);

      return routinesWithData;
    },
    enabled: !!planId && enabled,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
    retry: 2,
    retryDelay: 500
  });
};
